import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
//   widgets.exhibitionist.empty.group.title
//   widgets.exhibitionist.empty.group.subtitle
//   widgets.exhibitionist.empty.group.CTALabel
//   widgets.exhibitionist.empty.featured_gallery.owner.title
//   widgets.exhibitionist.empty.featured_gallery.owner.subtitle
//   widgets.exhibitionist.empty.featured_gallery.owner.CTALabel
//   widgets.exhibitionist.empty.featured_gallery.visitor.title
//   widgets.exhibitionist.empty.featured_gallery.visitor.subtitle
//   widgets.exhibitionist.empty.newest.owner.title
//   widgets.exhibitionist.empty.newest.owner.subtitle
//   widgets.exhibitionist.empty.newest.owner.CTALabel
//   widgets.exhibitionist.empty.newest.visitor.title
//   widgets.exhibitionist.empty.newest.visitor.subtitle
//   widgets.exhibitionist.empty.gallery.owner.title
//   widgets.exhibitionist.empty.gallery.owner.subtitle
//   widgets.exhibitionist.empty.gallery.owner.CTALabel
//   widgets.exhibitionist.empty.gallery.visitor.title
//   widgets.exhibitionist.empty.gallery.visitor.subtitle
//   widgets.exhibitionist.empty.collection.owner.title
//   widgets.exhibitionist.empty.collection.owner.subtitle
//   widgets.exhibitionist.empty.collection.owner.CTALabel
//   widgets.exhibitionist.empty.collection.visitor.title
//   widgets.exhibitionist.empty.collection.visitor.subtitle
//   widgets.exhibitionist.empty.private_collection.owner.title
//   widgets.exhibitionist.empty.private_collection.owner.subtitle
//   widgets.exhibitionist.empty.private_collection.owner.CTALabel
//   widgets.exhibitionist.empty.private_collection.visitor.title
//   widgets.exhibitionist.empty.private_collection.visitor.subtitle
//   widgets.exhibitionist.empty.adoptable_bought.owner.CTALabel
//   widgets.exhibitionist.empty.adoptable_for_sale.owner.CTALabel
//   widgets.exhibitionist.empty.featured_gallery.owner.dreamupCta.label
import c from 'classnames';
import { URLS, Url, sanitizeUrl } from '@wix/da-url';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import InfoBlock from '@wix/da-ds/pkg/InfoBlock';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import DreamUpIcon16 from '@wix/da-ds/pkg/Icons/16x16/DreamUp';
import useModulePrivileges from '@wix/da-gruser-shared/pkg/hooks/useModulePrivileges';
import useGruser from '@wix/da-gruser-shared/pkg/hooks/useGruser';
import { PapiModuleExhibitionist } from '@wix/da-papi-types';
import useModuleData from '@wix/da-gruser-shared/pkg/hooks/useModuleData';
import { useVerification } from '../../../VerificationPopup/withVerification';
import { isGroup } from '../../../User/helpers';
import { GallectionSourceType } from '../types';
import { useExhibitionistType } from '../hooks';
import { useSubmitUrl } from '../../../utils/hooks/useSubmitUrl';

import s from './ExhibitionistEmpty.scss';

const componentLocale = 'widgets.exhibitionist.empty';
const navigateTo = url => window.location.assign(sanitizeUrl(url));

export interface Props {
  moduleId: number;
}

const ExhibitionistEmpty: React.FC<Props> = ({ moduleId }) => {
  const { t } = useTranslation();
  const { aiContentFilter } = useContext(SessionContext);
  const exhibitionistType = useExhibitionistType();
  const { gruserOwner } = useGruser();
  const { restrictToVerified } = useVerification();
  const { moduleData } = useModuleData<PapiModuleExhibitionist>(moduleId);
  const modulePrivs = useModulePrivileges(moduleId);
  const SUBMIT_URL = useSubmitUrl(true);

  if (!gruserOwner) {
    return null;
  }

  const { sourceType } = moduleData;
  const isOwner = modulePrivs?.edit;
  const { username } = gruserOwner;
  const isGroupProfile = isGroup(gruserOwner);
  const viewer = isOwner ? 'owner' : 'visitor';
  const localePrefix = `${componentLocale}.${sourceType}`;
  const localeUsername = viewer === 'visitor' ? { username } : undefined;
  const title = t(
    isGroupProfile && isOwner
      ? `${componentLocale}.group.title`
      : `${localePrefix}.${viewer}.title`,
    localeUsername
  );
  const subtitle = t(
    isGroupProfile && isOwner
      ? `${componentLocale}.group.subtitle`
      : `${localePrefix}.${viewer}.subtitle`,
    localeUsername
  );

  const ownerCTALocale = `${localePrefix}.owner.CTALabel`;

  const extraProps = {};
  switch (sourceType) {
    case GallectionSourceType.COLLECTION:
      extraProps['buttonLabel'] = t(ownerCTALocale);
      extraProps['onButtonClick'] = () => navigateTo(URLS.ROOT);
      break;
    case GallectionSourceType.ADOPTABLE_BOUGHT:
      extraProps['buttonLabel'] = t(ownerCTALocale);
      extraProps['onButtonClick'] = () => navigateTo(URLS.SHOP);
      break;
    case GallectionSourceType.ADOPTABLE_FOR_SALE:
      extraProps['buttonLabel'] = t(ownerCTALocale);
      extraProps['onButtonClick'] = () => navigateTo(SUBMIT_URL);
      break;
    case GallectionSourceType.ORIGINALS_FOR_SALE:
      extraProps['buttonLabel'] = t(ownerCTALocale);
      extraProps['onButtonClick'] = () => navigateTo(Url.studio({ openCreateForm: true }));
      break;
    default:
      extraProps['buttonLabel'] = t(
        isGroupProfile && isOwner ? `${componentLocale}.group.CTALabel` : ownerCTALocale
      );
      extraProps['onButtonClick'] = restrictToVerified('submit', () =>
        navigateTo(Url.studio({ openSubmitForm: true }))
      );
      extraProps['buttonVariant'] = 'approval-green';
      extraProps['biData'] = {
        evid: 704,
        sectionname: 'exhibitionist',
      };
  }

  return (
    <InfoBlock
      className={c(s['root'], s[`${exhibitionistType}_${sourceType}`])}
      title={title}
      subtitle={subtitle}
      withButton={isOwner}
      customSecondaryButton={
        aiContentFilter !== 'suppress' &&
        viewer !== 'visitor' &&
        sourceType === GallectionSourceType.FEATURED_GALLERY && (
          <GhostButton variant="main" size="medium" icon={DreamUpIcon16} href={Url.dreamupLink()}>
            {t(`${localePrefix}.owner.dreamupCta.label`)}
          </GhostButton>
        )
      }
      {...extraProps}
    />
  );
};

export default ExhibitionistEmpty;
